import {runInAction} from 'mobx'
import React from 'react'
import {
  getScaleBoundsPadding,
  getScaleScrollDelta
} from '@components/timeline-new/utils'
import {TimelineNewStore} from './timeline-new-store'
import {Point} from './types'

export class GraphHelper {
  private startPoint: Point = {x: 0, y: 0}
  private graphMoving = false
  private scrollLeftFrom = 0
  private scrollTopFrom = 0

  constructor(private readonly store: TimelineNewStore) {
    this.scrollToToday = this.scrollToToday.bind(this)
    this.startGraphDrag = this.startGraphDrag.bind(this)
    this.graphMouseMove = this.graphMouseMove.bind(this)
    this.graphMouseUp = this.graphMouseUp.bind(this)
    this.graphScrollX = this.graphScrollX.bind(this)
    this.graphScrollY = this.graphScrollY.bind(this)
  }

  scrollToToday(): void {
    if (!this.store.state.diagramEl) {
      return
    }

    const {width} = this.store.state.diagramEl.getBoundingClientRect()
    const dateFrom = Date.now() - this.store.distanceToInterval(width) / 2
    this.resetBounds(dateFrom)
  }

  startGraphDrag(e: React.MouseEvent): void {
    if (
      e.target instanceof Element &&
      !!e.target.closest('[data-tippy-root]')
    ) {
      return
    }

    this.startPoint = {x: e.clientX, y: e.clientY}
    this.graphMoving = true
    this.subscribe()

    runInAction(() => {
      if (this.store.state.diagramEl) {
        this.scrollLeftFrom = this.store.state.diagramEl.scrollLeft
      }
      if (this.store.state.tableScrollableEl) {
        this.scrollTopFrom = this.store.state.tableScrollableEl.scrollTop
        this.store.setCursorClass('!cursor-grabbing')
      }
    })
  }

  private graphMouseMove(e: MouseEvent): void {
    e.stopPropagation()
    e.preventDefault()

    const currentPoint = {x: e.clientX, y: e.clientY}

    runInAction(() => {
      if (this.store.state.diagramEl) {
        const dx = this.startPoint.x - currentPoint.x
        this.store.state.diagramEl.scrollLeft = this.scrollLeftFrom + dx
      }

      if (this.store.state.tableScrollableEl) {
        const dy = this.startPoint.y - currentPoint.y
        this.store.state.tableScrollableEl.scrollTop = this.scrollTopFrom + dy
        this.store.onTableScrollY()
      }

      this.store.scheduleRecalc()
    })
  }

  private graphMouseUp(e: MouseEvent): void {
    e.stopPropagation()
    e.preventDefault()

    this.graphMoving = false
    this.startPoint = {x: 0, y: 0}
    this.unsubscribe()

    this.store.setCursorClass('')
  }

  graphScrollX(): void {
    const scrollableEl = this.store.state.diagramEl
    const containerEl = this.store.state.diagramContainerEl
    if (!scrollableEl || !containerEl) {
      return
    }

    const scrollableRect = scrollableEl.getBoundingClientRect()
    const containerRect = containerEl.getBoundingClientRect()
    const threshold = 50

    const dRight =
      containerRect.width - (scrollableEl.scrollLeft + scrollableRect.width)
    const dLeft = scrollableEl.scrollLeft
    if (dRight >= 0 && dRight < threshold) {
      const delta = getScaleScrollDelta(this.store.state.scale)
      this.store.state.rightBoundary += delta

      const dt = this.store.state.rightBoundary - this.store.state.leftBoundary
      const width = this.store.intervalToDistance(dt)
      containerEl.style.width = `${width}px`

      this.store.scheduleRecalc()
    } else if (dLeft >= 0 && dLeft < threshold) {
      const delta = getScaleScrollDelta(this.store.state.scale)
      this.store.state.leftBoundary -= delta

      const dt = this.store.state.rightBoundary - this.store.state.leftBoundary
      const width = this.store.intervalToDistance(dt)
      containerEl.style.width = `${width}px`

      const dWidth = width - containerRect.width
      scrollableEl.scrollLeft += dWidth
      if (this.graphMoving) {
        this.scrollLeftFrom += dWidth
      }

      this.store.scheduleRecalc()
    }
  }

  graphScrollY(e: WheelEvent): void {
    // Shift changes scroll axis, so Shift+wheel should scroll diagram only horizontally
    if (this.store.state.tableScrollableEl && !e.shiftKey) {
      this.store.state.tableScrollableEl.scrollTop += e.deltaY
      this.store.onTableScrollY()
    }
  }

  resetBounds(dateFrom: number): void {
    const {scale, leftThreshold, rightThreshold} = this.store.state
    const padding = getScaleBoundsPadding(scale)
    const width = this.store.state.diagramEl?.getBoundingClientRect().width ?? 0
    const dt = this.store.distanceToInterval(width)
    const start = this.store.startTimeToSnappedTime(dateFrom)
    const end = this.store.endTimeToSnappedTime(dateFrom + dt)
    this.store.state.leftBoundary = Math.min(start, leftThreshold) - padding
    this.store.state.rightBoundary = Math.max(end, rightThreshold) + padding

    const scrollableEl = this.store.state.diagramEl
    const containerEl = this.store.state.diagramContainerEl
    if (scrollableEl && containerEl) {
      const {leftBoundary, rightBoundary} = this.store.state
      const width = this.store.intervalToDistance(rightBoundary - leftBoundary)
      containerEl.style.width = `${width}px`

      const part = (dateFrom - leftBoundary) / (rightBoundary - leftBoundary)
      scrollableEl.scrollLeft = part * width
    }

    this.store.scheduleRecalc()
  }

  private subscribe(): void {
    document.addEventListener('mousemove', this.graphMouseMove, {capture: true})
    document.addEventListener('mouseup', this.graphMouseUp, {capture: true})
  }

  private unsubscribe(): void {
    document.removeEventListener('mousemove', this.graphMouseMove, {
      capture: true
    })
    document.removeEventListener('mouseup', this.graphMouseUp, {capture: true})
  }
}
